import { useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffectOnce } from 'react-use';

type UseTableHistoryResult<TRowId = string | number> = {
  initiallyExpandedRows: TRowId[];
  onToggle: (id: TRowId, isOpen: boolean) => void;
};

/*
 * Hook used to save table state in browser history.
 * This is necessary so that when traveling between tabs,
 * e.g Business Hours -> Go to Flow -> Remember which schedule was expanded when returning.
 */
export function useTableHistory<
  TRowId = string | number,
>(): UseTableHistoryResult<TRowId> {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    expandedRowItems = [],
  }: {
    expandedRowItems: TRowId[];
  } = location?.state || {};
  const state = useRef<Set<TRowId>>(new Set());

  const onToggle = useCallback<UseTableHistoryResult<TRowId>['onToggle']>(
    (id, isOpen) => {
      state.current[isOpen ? 'add' : 'delete'](id);

      navigate(location.pathname, {
        replace: true,
        state: { expandedRowItems: state.current },
      });
    },
    [location.pathname, navigate],
  );

  // Reset state once we mount, so that on subsequent refreshes it wouldn't expand.
  useEffectOnce(() => {
    if (expandedRowItems.length > 0) {
      navigate(location.pathname, { replace: true, state: null });
    }
  });

  return {
    initiallyExpandedRows: [...expandedRowItems],
    onToggle,
  };
}
